import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";
import {
  Eye, MoreVertical, ChevronLeft, ChevronRight, Trash2,
  ArrowLeft, BriefcaseIcon, Bookmark, MapPin,
  DollarSign, TrendingUp, CheckCircle2, XCircle, AlertCircle,
  Search, ExternalLink, Star, Calendar, Zap
} from "lucide-react";
import { APPLICATION_API_END_POINT, SAVED_JOBS_API_END_POINT } from "@/utils/constant";
import Navbar from "../shared/Navbar";
import Footer from "../Footer";
import PropTypes from "prop-types";

// ── Status config ──────────────────────────────────────────────────────────────
const STATUS_CONFIG = {
  accepted: {
    label: "Accepted", color: "text-emerald-700", bg: "bg-emerald-50", border: "border-emerald-200",
    dot: "bg-emerald-500", icon: CheckCircle2,
  },
  pending: {
    label: "Under Review", color: "text-amber-700", bg: "bg-amber-50", border: "border-amber-200",
    dot: "bg-amber-400", icon: AlertCircle,
  },
  rejected: {
    label: "Not Selected", color: "text-red-600", bg: "bg-red-50", border: "border-red-200",
    dot: "bg-red-400", icon: XCircle,
  },
};

const PIPELINE_STEPS = ["Applied", "Screening", "Interview", "Offer"];

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status?.toLowerCase()] || STATUS_CONFIG.pending;
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border ${cfg.bg} ${cfg.color} ${cfg.border}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot} animate-pulse`} />
      {cfg.label}
    </span>
  );
}
StatusBadge.propTypes = { status: PropTypes.string.isRequired };

function PipelineBar({ status }) {
  const s = status?.toLowerCase();
  const step = s === "accepted" ? 3 : 1;
  return (
    <div className="flex items-center gap-1 mt-1.5">
      {PIPELINE_STEPS.map((_, i) => (
        <div
          key={i}
          className={`h-1.5 w-8 rounded-full transition-all ${
            s === "rejected" && i === 1 ? "bg-red-400"
            : i <= step && s !== "rejected" ? "bg-[#012760]"
            : "bg-gray-200"
          }`}
        />
      ))}
      <span className="text-[10px] text-gray-400 ml-1">
        {s === "accepted" ? "Offer stage" : s === "rejected" ? "Closed" : "Screening"}
      </span>
    </div>
  );
}
PipelineBar.propTypes = { status: PropTypes.string.isRequired };

function StatCard({ icon: Icon, value, label, accent }) {
  const colorMap = {
    "border-blue-200":    { bg: "bg-blue-50",    icon: "text-blue-600" },
    "border-emerald-200": { bg: "bg-emerald-50",  icon: "text-emerald-600" },
    "border-amber-200":   { bg: "bg-amber-50",    icon: "text-amber-600" },
    "border-red-200":     { bg: "bg-red-50",      icon: "text-red-500" },
  };
  const c = colorMap[accent] || colorMap["border-blue-200"];
  return (
    <div className={`rounded-2xl p-5 border ${accent} bg-white flex items-center gap-4`}>
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${c.bg}`}>
        <Icon className={`w-5 h-5 ${c.icon}`} />
      </div>
      <div>
        <div className="text-2xl font-bold text-gray-900">{value}</div>
        <div className="text-xs text-gray-500 font-medium mt-0.5">{label}</div>
      </div>
    </div>
  );
}
StatCard.propTypes = {
  icon: PropTypes.elementType.isRequired,
  value: PropTypes.number.isRequired,
  label: PropTypes.string.isRequired,
  accent: PropTypes.string.isRequired,
};

// ── Main Page ──────────────────────────────────────────────────────────────────
function MyJobsPage() {
  const navigate = useNavigate();
  const { user } = useSelector((store) => store.auth);

  const [activeTab, setActiveTab]       = useState("applied");
  const [allAppliedJobs, setAllAppliedJobs] = useState([]);
  const [savedJobs, setSavedJobs]       = useState([]);
  const [isLoading, setIsLoading]       = useState(true);
  const [error, setError]               = useState(null);
  const [currentPage, setCurrentPage]   = useState(1);
  const [search, setSearch]             = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [expandedRow, setExpandedRow]   = useState(null);
  const jobsPerPage = 5;

  useEffect(() => {
    if (!user) { navigate("/login"); return; }
    if (activeTab === "applied") fetchAppliedJobs();
    else fetchSavedJobs();
  }, [activeTab, navigate, user]);

  const fetchAppliedJobs = async () => {
    try {
      setIsLoading(true);
      const res = await axios.get(`${APPLICATION_API_END_POINT}/get`, { withCredentials: true });
      if (res.data.status) {
        setAllAppliedJobs(
          res.data.applications?.map((app) => ({
            id:          app?.id,
            date:        new Date(app?.createdAt)?.toLocaleDateString() || "N/A",
            role:        app?.job?.title || "Not Specified",
            company:     app?.job?.company?.CompanyName || "Unknown",
            status:      app?.status || "Pending",
            location:    app?.job?.location || "Remote",
            companyLogo: app?.job?.company?.logo || "/default-company-logo.png",
            salary:      app?.job?.salary || "Not Disclosed",
            jobType:     Array.isArray(app?.job?.jobType) ? app.job.jobType : [],
            experience:  app?.job?.experience || "Not Mentioned",
          })) || []
        );
      }
    } catch (err) {
      setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSavedJobs = async () => {
    try {
      setIsLoading(true);
      const res = await axios.get(`${SAVED_JOBS_API_END_POINT}/saved`, { withCredentials: true });
      setSavedJobs(res.data.savedJobs || []);
    } catch (err) {
      setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveSavedJob = async (jobId) => {
    try {
      await axios.delete(`${SAVED_JOBS_API_END_POINT}/saved`, { data: { jobId }, withCredentials: true });
      setSavedJobs((prev) => prev.filter((j) => j.savedJobId !== jobId));
    } catch (err) {
      console.error("Error removing saved job:", err);
    }
  };

  // Derived stats
  const accepted = allAppliedJobs.filter((j) => j.status?.toLowerCase() === "accepted").length;
  const pending  = allAppliedJobs.filter((j) => j.status?.toLowerCase() === "pending").length;
  const rejected = allAppliedJobs.filter((j) => j.status?.toLowerCase() === "rejected").length;

  // Filter + paginate applied jobs
  const filteredApplied = allAppliedJobs.filter((j) => {
    const matchSearch =
      j.role.toLowerCase().includes(search.toLowerCase()) ||
      j.company.toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === "all" || j.status?.toLowerCase() === statusFilter;
    return matchSearch && matchStatus;
  });

  const totalJobs     = activeTab === "applied" ? filteredApplied.length : savedJobs.length;
  const indexOfLast   = currentPage * jobsPerPage;
  const indexOfFirst  = indexOfLast - jobsPerPage;
  const currentJobs   = activeTab === "applied"
    ? filteredApplied.slice(indexOfFirst, indexOfLast)
    : savedJobs.slice(indexOfFirst, indexOfLast);
  const totalPages    = Math.ceil(totalJobs / jobsPerPage);

  const tabs = [
    { id: "applied", label: "Applied Jobs", icon: BriefcaseIcon, count: allAppliedJobs.length },
    { id: "saved",   label: "Saved Jobs",   icon: Bookmark,      count: savedJobs.length },
  ];

  // ── Applied Table ────────────────────────────────────────────────────────────
  const renderAppliedTable = () => (
    <table className="w-full">
      <thead>
        <tr className="bg-gray-50/70 border-b border-gray-100">
          {["Job", "Company", "Date Applied", "Type", "Status", "Actions"].map((h, i) => (
            <th
              key={h}
              className={`text-left text-xs font-semibold text-gray-500 px-5 py-3 uppercase tracking-wide
                ${i === 1 ? "hidden md:table-cell" : ""}
                ${i === 2 || i === 3 ? "hidden lg:table-cell" : ""}
                ${i === 5 ? "text-right" : ""}
              `}
            >{h}</th>
          ))}
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-50">
        {currentJobs.length > 0 ? currentJobs.map((job) => (
          <>
            <tr
              key={job.id}
              className="hover:bg-gray-50/80 cursor-pointer transition-colors"
              onClick={() => setExpandedRow(expandedRow === job.id ? null : job.id)}
            >
              {/* Job */}
              <td className="px-5 py-4">
                <div className="flex items-center gap-3">
                  <div className="relative flex-shrink-0">
                    <img
                      src={job.companyLogo}
                      alt={job.company}
                      className="w-11 h-11 rounded-xl object-cover ring-1 ring-gray-100"
                    />
                    {job.status?.toLowerCase() === "accepted" && (
                      <span className="absolute -bottom-1 -right-1 w-4 h-4 bg-emerald-500 rounded-full border-2 border-white flex items-center justify-center">
                        <CheckCircle2 className="w-2.5 h-2.5 text-white" />
                      </span>
                    )}
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900 text-sm leading-snug">{job.role}</div>
                    <div className="flex items-center gap-1 text-xs text-gray-400 mt-0.5">
                      <MapPin className="w-3 h-3" />{job.location}
                      {job.salary && <><span className="text-gray-300">•</span><span>{job.salary}</span></>}
                    </div>
                    <PipelineBar status={job.status} />
                  </div>
                </div>
              </td>

              {/* Company */}
              <td className="px-5 py-4 hidden md:table-cell">
                <span className="text-sm text-gray-700 font-medium">{job.company}</span>
              </td>

              {/* Date */}
              <td className="px-5 py-4 hidden lg:table-cell">
                <div className="flex items-center gap-1.5 text-sm text-gray-600">
                  <Calendar className="w-3.5 h-3.5 text-gray-400" />{job.date}
                </div>
              </td>

              {/* Type */}
              <td className="px-5 py-4 hidden lg:table-cell">
                {job.jobType.map((t) => (
                  <span key={t} className="inline-block px-2.5 py-1 rounded-lg bg-blue-50 text-[#012760] text-xs font-semibold mr-1">{t}</span>
                ))}
              </td>

              {/* Status */}
              <td className="px-5 py-4">
                <StatusBadge status={job.status} />
              </td>

              {/* Actions */}
              <td className="px-5 py-4 text-right">
                <div className="flex items-center justify-end gap-1">
                  <button className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-400 hover:text-[#012760] transition-colors">
                    <Eye className="w-4 h-4" />
                  </button>
                  <button className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-400 transition-colors">
                    <MoreVertical className="w-4 h-4" />
                  </button>
                </div>
              </td>
            </tr>

            {/* Expanded detail row */}
            {expandedRow === job.id && (
              <tr key={`${job.id}-exp`} className="bg-blue-50/40">
                <td colSpan={6} className="px-5 pb-5 pt-2">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                    {/* Timeline */}
                    <div className="bg-white rounded-xl p-4 border border-blue-100">
                      <div className="text-xs font-semibold text-gray-400 uppercase mb-3">Application Timeline</div>
                      {["Applied", "Profile Viewed", "Under Review"].map((step, i) => (
                        <div key={step} className="flex items-center gap-2 py-1.5">
                          <div className={`w-2 h-2 rounded-full flex-shrink-0 ${i < 2 ? "bg-[#012760]" : "bg-gray-200"}`} />
                          <span className={`text-xs ${i < 2 ? "text-gray-700 font-medium" : "text-gray-400"}`}>{step}</span>
                          {i < 2 && <span className="text-[10px] text-gray-400 ml-auto">{job.date}</span>}
                        </div>
                      ))}
                    </div>

                    {/* Job details */}
                    <div className="bg-white rounded-xl p-4 border border-blue-100">
                      <div className="text-xs font-semibold text-gray-400 uppercase mb-3">Job Details</div>
                      <div className="space-y-2 text-xs text-gray-600">
                        <div className="flex items-center gap-1.5"><DollarSign className="w-3.5 h-3.5 text-gray-400" />{job.salary}</div>
                        <div className="flex items-center gap-1.5"><TrendingUp className="w-3.5 h-3.5 text-gray-400" />{job.experience} yrs experience</div>
                        <div className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-gray-400" />{job.location}</div>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {job.jobType.map((t) => (
                            <span key={t} className="px-2 py-0.5 bg-blue-50 text-[#012760] rounded-md text-[11px] font-semibold">{t}</span>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Quick actions */}
                    <div className="bg-white rounded-xl p-4 border border-blue-100">
                      <div className="text-xs font-semibold text-gray-400 uppercase mb-3">Quick Actions</div>
                      <div className="space-y-2">
                        <button className="w-full flex items-center gap-2 text-xs font-medium text-[#012760] bg-blue-50 hover:bg-blue-100 px-3 py-2 rounded-lg transition-colors">
                          <Eye className="w-3.5 h-3.5" /> View Job Posting
                        </button>
                        <button className="w-full flex items-center gap-2 text-xs font-medium text-gray-600 bg-gray-50 hover:bg-gray-100 px-3 py-2 rounded-lg transition-colors">
                          <ExternalLink className="w-3.5 h-3.5" /> Company Profile
                        </button>
                      </div>
                    </div>
                  </div>
                </td>
              </tr>
            )}
          </>
        )) : (
          <tr>
            <td colSpan={6} className="text-center py-16">
              <BriefcaseIcon className="w-10 h-10 mx-auto mb-3 text-gray-200" />
              <p className="text-gray-400 text-sm">
                {search || statusFilter !== "all" ? "No jobs match your filters." : "No applied jobs yet."}
              </p>
              {!search && statusFilter === "all" && (
                <button
                  onClick={() => navigate("/jobs")}
                  className="mt-4 bg-[#012760] hover:bg-[#012760]/90 text-white px-5 py-2.5 rounded-xl text-sm font-semibold"
                >
                  Browse Jobs
                </button>
              )}
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );

  // ── Saved Jobs Cards ─────────────────────────────────────────────────────────
  const renderSavedCards = () => (
    <div className="p-6 grid grid-cols-1 gap-4">
      {currentJobs.length > 0 ? currentJobs.map((job) => (
        <div
          key={job.savedJobId}
          className="border border-gray-100 rounded-2xl p-5 hover:border-[#012760]/30 hover:shadow-sm transition-all bg-white/50 flex flex-col sm:flex-row gap-4"
        >
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <img
              src={job.jobDetails?.company?.logo || "/default-company-logo.png"}
              alt={job.jobDetails?.company?.CompanyName}
              className="w-12 h-12 rounded-xl ring-1 ring-gray-100 flex-shrink-0 object-cover"
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <h3 className="font-semibold text-gray-900 text-sm">{job.jobDetails?.title}</h3>
                  <p className="text-xs text-gray-500 mt-0.5 font-medium">{job.jobDetails?.company?.CompanyName}</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-3 mt-2 text-xs text-gray-500">
                {job.jobDetails?.location && (
                  <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{job.jobDetails.location}</span>
                )}
                {job.jobDetails?.salary && (
                  <span className="flex items-center gap-1"><DollarSign className="w-3 h-3" />${job.jobDetails.salary}</span>
                )}
                {job.jobDetails?.experience && (
                  <span className="flex items-center gap-1"><TrendingUp className="w-3 h-3" />{job.jobDetails.experience} yrs</span>
                )}
              </div>
              {job.jobDetails?.description && (
                <p className="text-xs text-gray-500 mt-2 line-clamp-2">{job.jobDetails.description}</p>
              )}
              {job.jobDetails?.requirements?.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {job.jobDetails.requirements.map((r, i) => (
                    <span key={i} className="px-2.5 py-1 rounded-lg bg-gray-100 text-gray-600 text-[11px] font-medium">{r}</span>
                  ))}
                </div>
              )}
              {job.jobDetails?.jobType?.length > 0 && (
                <div className="flex gap-1.5 mt-2">
                  {job.jobDetails.jobType.map((t) => (
                    <span key={t} className="px-2.5 py-1 rounded-lg bg-blue-50 text-[#012760] text-[11px] font-semibold">{t}</span>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="flex flex-row sm:flex-col gap-2 justify-end items-end flex-shrink-0">
            <button
              onClick={() => navigate(`/description/${job.jobId}`)}
              className="flex items-center gap-1.5 bg-[#012760] text-white px-4 py-2 rounded-xl text-xs font-semibold hover:bg-[#012760]/90 transition-all whitespace-nowrap"
            >
              <Star className="w-3 h-3" /> View & Apply
            </button>
            <button
              onClick={() => handleRemoveSavedJob(job.jobId)}
              className="flex items-center gap-1.5 border border-red-200 text-red-500 px-4 py-2 rounded-xl text-xs font-semibold hover:bg-red-50 transition-all whitespace-nowrap"
            >
              <Trash2 className="w-3 h-3" /> Remove
            </button>
          </div>
        </div>
      )) : (
        <div className="text-center py-16">
          <Bookmark className="w-10 h-10 mx-auto mb-3 text-gray-200" />
          <p className="text-gray-400 text-sm">No saved jobs found.</p>
          <button
            onClick={() => navigate("/jobs")}
            className="mt-4 bg-[#012760] hover:bg-[#012760]/90 text-white px-5 py-2.5 rounded-xl text-sm font-semibold"
          >
            Browse Jobs
          </button>
        </div>
      )}
    </div>
  );

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-[#f5f7fa]">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
        body { font-family: 'Plus Jakarta Sans', sans-serif; }
        .fade-in { animation: fadeIn 0.35s ease both; }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }
      `}</style>

      <Navbar />

      <div className="container mx-auto px-6 py-10 mt-24 max-w-6xl fade-in">

        {/* Back */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-sm text-gray-500 hover:text-[#012760] font-medium mb-6 transition-colors -ml-1"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Profile
        </button>

        {/* Header */}
        <div className="flex items-start justify-between mb-8">
          <div>
            <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight">My Jobs</h1>
            <p className="text-gray-500 text-sm mt-1">Track your applications and saved opportunities</p>
          </div>
          <button
            onClick={() => navigate("/jobs")}
            className="flex items-center gap-2 bg-[#012760] text-white px-5 py-2.5 rounded-xl text-sm font-semibold hover:bg-[#012760]/90 transition-all shadow-sm"
          >
            <BriefcaseIcon className="w-4 h-4" /> Browse Jobs
          </button>
        </div>

        {/* Stats — only show when applied tab has loaded data */}
        {!isLoading && activeTab === "applied" && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <StatCard icon={BriefcaseIcon} value={allAppliedJobs.length} label="Total Applied"   accent="border-blue-200" />
            <StatCard icon={CheckCircle2}  value={accepted}              label="Accepted"         accent="border-emerald-200" />
            <StatCard icon={AlertCircle}   value={pending}               label="Under Review"     accent="border-amber-200" />
            <StatCard icon={XCircle}       value={rejected}              label="Not Selected"     accent="border-red-200" />
          </div>
        )}

        {/* Main Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">

          {/* Tabs + search/filter bar */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 px-6 pt-5 pb-0 border-b border-gray-100">
            <div className="flex gap-1">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                const active = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => { setActiveTab(tab.id); setCurrentPage(1); setSearch(""); setStatusFilter("all"); }}
                    className={`flex items-center gap-2 px-5 py-3 text-sm font-semibold rounded-t-lg transition-all border-b-2 -mb-px ${
                      active ? "border-[#012760] text-[#012760]" : "border-transparent text-gray-400 hover:text-gray-600"
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {tab.label}
                    {!isLoading && (
                      <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${active ? "bg-[#012760] text-white" : "bg-gray-100 text-gray-500"}`}>
                        {tab.count}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Search + filter (applied tab) */}
            {activeTab === "applied" && !isLoading && (
              <div className="flex items-center gap-2 pb-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search role or company…"
                    value={search}
                    onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
                    className="pl-8 pr-4 py-2 text-xs border border-gray-200 rounded-lg w-52 focus:outline-none focus:border-[#012760] focus:ring-1 focus:ring-[#012760]/20"
                  />
                </div>
                <select
                  value={statusFilter}
                  onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
                  className="text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-[#012760] text-gray-600 bg-white cursor-pointer"
                >
                  <option value="all">All Status</option>
                  <option value="pending">Under Review</option>
                  <option value="accepted">Accepted</option>
                  <option value="rejected">Not Selected</option>
                </select>
              </div>
            )}
          </div>

          {/* Content */}
          <div>
            {isLoading ? (
              <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-[#012760]" />
              </div>
            ) : error ? (
              <div className="text-red-500 text-center p-8 text-sm">
                Error loading jobs: {error.message}
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  {activeTab === "applied" ? renderAppliedTable() : renderSavedCards()}
                </div>

                {/* Pagination */}
                {totalJobs > jobsPerPage && (
                  <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100">
                    <span className="text-xs text-gray-500">
                      Showing {indexOfFirst + 1}–{Math.min(indexOfLast, totalJobs)} of {totalJobs} jobs
                    </span>
                    <div className="flex gap-1">
                      <button
                        onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                        disabled={currentPage === 1}
                        className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-500 hover:bg-gray-100 disabled:opacity-30 transition-all"
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </button>
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                        <button
                          key={p}
                          onClick={() => setCurrentPage(p)}
                          className={`w-8 h-8 rounded-lg text-xs font-semibold transition-all ${
                            p === currentPage ? "bg-[#012760] text-white" : "text-gray-500 hover:bg-gray-100"
                          }`}
                        >
                          {p}
                        </button>
                      ))}
                      <button
                        onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                        disabled={currentPage === totalPages}
                        className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-500 hover:bg-gray-100 disabled:opacity-30 transition-all"
                      >
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Boost banner */}
        <div className="mt-6 bg-gradient-to-r from-[#012760] to-[#0144a8] rounded-2xl p-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center">
              <Zap className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="text-white font-semibold text-sm">Boost your profile visibility</div>
              <div className="text-white/70 text-xs mt-0.5">Add skills and certifications to stand out to recruiters</div>
            </div>
          </div>
          <button
            onClick={() => navigate("/profile")}
            className="bg-white text-[#012760] px-5 py-2.5 rounded-xl text-xs font-bold hover:bg-gray-50 transition-all flex-shrink-0"
          >
            Update Profile
          </button>
        </div>

      </div>

      <Footer />
    </div>
  );
}

export default MyJobsPage;
